import { DataButton, InputCheckbox } from "@sellar-io/components";
import {
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Listbox,
} from "@headlessui/react";
import { truncate } from "@sellar-io/common";
import { ReactNode, useState } from "react";
import { useMatch } from "react-router";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { Icon, BuyerOnly } from "../../../components";
import { SPOTLIGHT } from "../../../constants/routes";

import {
  abvFilterType,
  AbvType,
  DietaryType,
  dietaryTypes,
} from "../../discovery/v2/DiscoveryV2SearchStore";
import { useGetSpotlightPageData } from "../Spotlight.shared";
import { useSpotlightFilterStore } from "../SpotlightFilterStore";

export const MoreSpotlightFilters = ({
  onDismiss,
  setClickOutside,
  children,
}: {
  onDismiss: () => void;
  setClickOutside?: (value?: boolean) => void;
  children?: ReactNode;
}) => {
  const pageId = useMatch(SPOTLIGHT.pattern)?.params.spotlightId;
  const { data } = useGetSpotlightPageData(pageId ?? "");

  const currentPage = pageId ? data : null;
  const state = useSpotlightFilterStore(useShallow((state) => state));

  const [selectedAbvTypes, setAbvTypes] = useState<AbvType[]>(
    state.selectedAbvTypes,
  );

  const [selectedDietaryTypes, setSelectedDietaryTypes] = useState<
    DietaryType[]
  >(state.selectedDietaryTypes);

  const [offersSellarCreditOnly, setOffersSellarCreditOnly] = useState<boolean>(
    state.offersSellarCreditOnly,
  );

  const canClearAll =
    offersSellarCreditOnly ||
    selectedDietaryTypes.length ||
    selectedAbvTypes.length;

  const onApply = (reset: boolean) => {
    if (reset) {
      useSpotlightFilterStore.getState().reset();
    } else {
      useSpotlightFilterStore.getState().setState({
        ...state,
        selectedAbvTypes,
        selectedDietaryTypes,
        offersSellarCreditOnly,
      });
    }
    onDismiss();
  };

  const showAbv = !currentPage?.hiddenFilters?.includes("abv");

  return (
    <>
      <div className={twMerge("flex w-full flex-col gap-4 p-4")}>
        <span className="font-semibold">Filter by</span>
        <div className="flex flex-col gap-4">
          {showAbv && (
            <div className="grid grid-cols-10 items-center">
              <p className={"col-span-4 text-zinc-500"}>ABV</p>
              <div className="col-span-6 flex w-full flex-col">
                <Listbox
                  value={selectedAbvTypes}
                  onChange={setAbvTypes}
                  multiple
                >
                  <ListboxButton
                    onClick={() => {
                      if (setClickOutside) setClickOutside(false);
                    }}
                    className={
                      "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                    }
                  >
                    {selectedAbvTypes.length
                      ? truncate(
                          selectedAbvTypes
                            .map(
                              (selectedType) =>
                                abvFilterType[selectedType].name,
                            )
                            .join(", "),
                        )
                      : "All %"}
                    <Icon
                      icon="CHEVRON_DOWN"
                      size={0.8}
                      className="absolute right-2"
                    />
                  </ListboxButton>
                  <ListboxOptions
                    className={
                      "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                    }
                    anchor="bottom start"
                  >
                    <ListboxOption
                      key={"x1"}
                      value={false}
                      disabled={true}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      Select multiple...
                    </ListboxOption>
                    {Object.keys(abvFilterType).map((abvTypeKey, i) => (
                      <ListboxOption
                        key={i}
                        value={abvTypeKey}
                        className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                      >
                        {selectedAbvTypes.includes(abvTypeKey) ? (
                          <Icon icon="CHECK" size={0.7} />
                        ) : (
                          <span className="w-[16.8px]" />
                        )}
                        {abvFilterType[abvTypeKey as AbvType].name}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Listbox>
              </div>
            </div>
          )}
          <div className="grid grid-cols-10 items-center">
            <p className={"col-span-4 text-zinc-500"}>Dietary types</p>
            <div className="col-span-6 flex w-full flex-col">
              <Listbox
                value={selectedDietaryTypes}
                onChange={setSelectedDietaryTypes}
                multiple
              >
                <ListboxButton
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                >
                  {selectedDietaryTypes.length
                    ? truncate(
                        selectedDietaryTypes
                          .map((type) => dietaryTypes[type])
                          .join(", "),
                      )
                    : "All dietary types"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {Object.keys(dietaryTypes).map((dietaryType, i) => (
                    <ListboxOption
                      key={i}
                      value={dietaryType}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      {selectedDietaryTypes.includes(dietaryType) ? (
                        <Icon icon="CHECK" size={0.7} />
                      ) : (
                        <span className="w-[16.8px]" />
                      )}
                      {dietaryTypes[dietaryType as DietaryType]}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>
          <BuyerOnly>
            <div className="grid grid-cols-10 items-center">
              <div className={"col-span-4"}></div>
              <div className="col-span-6 inline-flex gap-1">
                <InputCheckbox
                  checked={offersSellarCreditOnly}
                  id="confirm-association"
                  onChange={() =>
                    setOffersSellarCreditOnly(!offersSellarCreditOnly)
                  }
                  className="flex-none cursor-pointer"
                />
                <p className="text-sm">Offers</p>
                <p className="flex items-center gap-1 bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text text-sm font-semibold text-nowrap text-transparent">
                  <Icon
                    icon="SELLAR_NEW"
                    size={0.6669}
                    color="#D213A6"
                    className="shrink-0"
                  />
                  Sellar Credit
                </p>
              </div>
            </div>
          </BuyerOnly>
          {children}
          <div className="flex flex-row gap-2">
            <DataButton
              onClick={() => onApply(false)}
              border={true}
              disabled={!canClearAll}
            >
              Apply
            </DataButton>
            {!!canClearAll && (
              <DataButton onClick={() => onApply(true)}>Clear all</DataButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
